import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDate, formatMoney } from '../ui/format';
import { Badge, ErrorState, Loading } from '../ui/Page';
import {
  acceptPublicProposal,
  declinePublicProposal,
  proposalTone,
  viewPublicProposal,
} from './api';

/**
 * The page a customer sees (ADR 0012).
 *
 * No session, no navigation, no product chrome: a person who was sent a link should see the
 * proposal and two buttons. The PDF is offered because a proposal is something people forward to
 * whoever signs, and the figures on screen are the same bytes as the file — the hash on the
 * acceptance says so.
 */
export function ProposalAcceptPage(): React.JSX.Element {
  const { token = '' } = useParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [declining, setDeclining] = useState(false);
  const [done, setDone] = useState<{ status: string; contractNumber?: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const proposal = useQuery({
    queryKey: ['public-proposal', token],
    queryFn: () => viewPublicProposal(token),
    enabled: token !== '',
    retry: false,
  });

  const accept = useMutation({
    mutationFn: () => acceptPublicProposal(token, { name: name.trim(), email: email.trim() }),
    onSuccess: (result) => {
      setError(null);
      setDone(result);
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const decline = useMutation({
    mutationFn: () => declinePublicProposal(token, reason.trim()),
    onSuccess: (result) => {
      setError(null);
      setDone(result);
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  if (proposal.isLoading) return <Loading />;
  if (proposal.error)
    return <ErrorState error={proposal.error} onRetry={() => void proposal.refetch()} />;
  if (!proposal.data) return <Loading />;

  const data = proposal.data;

  return (
    <main className="public">
      <article className="public__card">
        <header className="public__head">
          <p className="muted small">{data.from}</p>
          <h1>{t('public.proposal').replace('{n}', String(data.number))}</h1>
          <p className="muted">{data.title}</p>
          {data.to && <p className="muted small">{t('public.for').replace('{name}', data.to)}</p>}
        </header>

        {data.introduction && <p className="public__intro">{data.introduction}</p>}

        <table className="table">
          <thead>
            <tr>
              <th>{t('deal.item')}</th>
              <th className="numeric">{t('deal.quantity')}</th>
              <th className="numeric">{t('proposals.total')}</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((item, index) => (
              <tr key={index}>
                <td>
                  {item.description}
                  {item.recurring && (
                    <span className="muted small">{` · ${t('proposals.monthly')}`}</span>
                  )}
                </td>
                <td className="numeric">{item.quantity}</td>
                <td className="numeric">{formatMoney(item.totalCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <dl className="facts public__totals">
          {data.recurringCents > 0 && (
            <>
              <dt>{t('proposals.recurring')}</dt>
              <dd>{formatMoney(data.recurringCents)}</dd>
            </>
          )}
          {data.oneOffCents > 0 && (
            <>
              <dt>{t('proposals.oneOff')}</dt>
              <dd>{formatMoney(data.oneOffCents)}</dd>
            </>
          )}
          <dt className="strong">{t('proposals.total')}</dt>
          <dd className="strong">{formatMoney(data.totalCents)}</dd>
          {data.termMonths > 0 && (
            <>
              <dt>{t('proposals.term')}</dt>
              <dd>{t('proposals.months').replace('{n}', String(data.termMonths))}</dd>
            </>
          )}
          {data.expiresAt && (
            <>
              <dt>{t('proposals.validUntil')}</dt>
              <dd>{formatDate(data.expiresAt)}</dd>
            </>
          )}
        </dl>

        {data.terms && (
          <section>
            <h2>{t('proposals.terms')}</h2>
            <p className="public__terms">{data.terms}</p>
          </section>
        )}

        {data.documentUrl && (
          <p>
            <a
              className="button button--quiet button--compact"
              href={data.documentUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('public.downloadPdf')}
            </a>
          </p>
        )}

        {done ? (
          <p className="alert alert--positive">
            {done.status === 'ACCEPTED'
              ? t('public.accepted').replace('{n}', String(done.contractNumber ?? ''))
              : t('public.declined')}
          </p>
        ) : !data.open ? (
          <p className="muted">
            <Badge tone={proposalTone(data.status)}>{t(`proposals.status.${data.status}`)}</Badge>{' '}
            {t('public.closed')}
          </p>
        ) : (
          <section className="public__decide">
            {error && (
              <p className="alert" role="alert">
                {error}
              </p>
            )}

            {!declining ? (
              <>
                <div className="form-grid">
                  <label className="stack__field">
                    <span>{t('public.yourName')}</span>
                    <input
                      value={name}
                      maxLength={200}
                      onChange={(event) => setName(event.target.value)}
                    />
                  </label>
                  <label className="stack__field">
                    <span>{t('public.yourEmail')}</span>
                    <input
                      type="email"
                      value={email}
                      maxLength={320}
                      onChange={(event) => setEmail(event.target.value)}
                    />
                  </label>
                </div>

                <p className="muted small">{t('public.acceptHint')}</p>

                <div className="cell-inline">
                  <button
                    type="button"
                    className="button button--primary"
                    disabled={name.trim().length < 2 || email.trim() === '' || accept.isPending}
                    onClick={() => accept.mutate()}
                  >
                    {t('public.accept')}
                  </button>
                  <button
                    type="button"
                    className="button button--quiet"
                    onClick={() => setDeclining(true)}
                  >
                    {t('public.declineInstead')}
                  </button>
                </div>
              </>
            ) : (
              <>
                <label className="stack__field">
                  <span>{t('public.declineReason')}</span>
                  <textarea
                    rows={3}
                    value={reason}
                    maxLength={1000}
                    onChange={(event) => setReason(event.target.value)}
                  />
                </label>
                <div className="cell-inline">
                  <button
                    type="button"
                    className="button"
                    disabled={decline.isPending}
                    onClick={() => decline.mutate()}
                  >
                    {t('public.decline')}
                  </button>
                  <button
                    type="button"
                    className="button button--quiet"
                    onClick={() => setDeclining(false)}
                  >
                    {t('confirm.cancel')}
                  </button>
                </div>
              </>
            )}
          </section>
        )}
      </article>
    </main>
  );
}
